// useFeedback.ts — open/close state and submission for the in-app feedback form.
//
// The dialog is mounted once in App.vue, so open state lives at module scope:
// any component (sidebar, a "report this" link in an error state) can call
// `openFeedback()` without prop-drilling or an event bus.
//
// Text only — no screenshots. Images of this app carry the user's balances and
// transactions, and the issues these become live on GitHub, outside the
// encryption boundary the rest of the app keeps.

import { ref } from 'vue'
import { authenticatedGet, authenticatedPost, type AuthContext } from '@/utils/api'

export type FeedbackType = 'bug' | 'idea' | 'question'

export interface FeedbackSubmission {
  id: number
  type: FeedbackType
  title: string
  status: 'posted' | 'pending' | 'failed'
  issue_number: number | null
  issue_url: string | null
  submitted_at: string | null
}

export interface FeedbackDraft {
  type: FeedbackType
  title: string
  body: string
}

/** Client context sent alongside a submission. The server whitelists these
 *  keys, so adding one here also needs a matching entry in
 *  `feedback_routes._ALLOWED_CONTEXT_KEYS` or it's silently dropped. */
export interface FeedbackContext {
  app_version: string
  route: string
  viewport: string
  display_mode: string
  theme: string
  high_contrast: boolean
  user_agent: string
  language: string
  timezone: string
}

const isOpen = ref(false)

export function openFeedback(): void {
  isOpen.value = true
}

export function closeFeedback(): void {
  isOpen.value = false
}

/** Gather the environment details that turn "it's broken" into a fixable
 *  report. Deliberately excludes anything about the user's finances — this is
 *  browser/build metadata only. */
export function collectContext(routePath: string): FeedbackContext {
  const root = typeof document !== 'undefined' ? document.documentElement : null
  const standalone =
    typeof window !== 'undefined' &&
    window.matchMedia?.('(display-mode: standalone)').matches

  return {
    app_version: typeof __APP_VERSION__ === 'string' ? __APP_VERSION__ : 'unknown',
    route: routePath,
    viewport:
      typeof window !== 'undefined'
        ? `${window.innerWidth}x${window.innerHeight} @${window.devicePixelRatio || 1}x`
        : 'unknown',
    display_mode: standalone ? 'standalone (installed)' : 'browser',
    theme: root?.getAttribute('data-theme') || 'unknown',
    high_contrast: root?.getAttribute('data-contrast') === 'high',
    user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : 'unknown',
    language: typeof navigator !== 'undefined' ? navigator.language : 'unknown',
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'unknown',
  }
}

export function useFeedback() {
  const submitting = ref(false)
  const error = ref<string | null>(null)
  const submitted = ref<FeedbackSubmission | null>(null)

  async function submit(
    draft: FeedbackDraft,
    context: FeedbackContext,
    auth: AuthContext,
  ): Promise<boolean> {
    submitting.value = true
    error.value = null
    try {
      const res = await authenticatedPost(
        '/api/feedback',
        { ...draft, context },
        auth,
      )
      const data = await res.json().catch(() => ({}))
      if (!res.ok) {
        error.value = data?.error || 'Could not send your feedback. Please try again.'
        return false
      }
      submitted.value = data.feedback ?? null
      return true
    } catch (e) {
      console.error('[feedback] submit failed:', e)
      error.value = 'Could not reach the server. Please try again.'
      return false
    } finally {
      submitting.value = false
    }
  }

  async function listMine(auth: AuthContext): Promise<FeedbackSubmission[]> {
    try {
      const res = await authenticatedGet('/api/feedback', auth)
      if (!res.ok) return []
      const data = await res.json()
      return data.feedback ?? []
    } catch (e) {
      console.error('[feedback] list failed:', e)
      return []
    }
  }

  function reset(): void {
    error.value = null
    submitted.value = null
    submitting.value = false
  }

  return { isOpen, submitting, error, submitted, submit, listMine, reset }
}
